import React from 'react'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import styled from 'styled-components'
import { Redirect } from 'react-router-dom'
import shortid from 'shortid'
import posthog from 'posthog-js'
import { addRecentGame, getCachedGame } from '../../utils/recentGames'
import GameService from '../../services/game'
import Crossword from '../crossword/crossword'
import { celebrate } from '../../utils/confetti'
import { COLORS } from '../../utils/colors'
import useLocalStorage from '../../hooks/useLocalStorage'
import ShareButton from './share-button'
import HomeButton from './home-button'
import useDuration from './use-duration'
import './game.scss'

const SOCKET_URL = process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:3000'
const OFFLINE_ID = -1
const RECONNECT_INTERVAL_MS = 3000

const StyledShareButton = styled(ShareButton)`
    position: absolute;
    right: 8%;
    cursor: pointer;

    @media screen and (min-width: 1000px) {
        right: 25%;
    }
`

const StyledHomeButton = styled(HomeButton)`
    position: absolute;
    left: 8%;
    cursor: pointer;

    @media screen and (min-width: 1000px) {
        left: 25%;
    }
`

const Game = ({ match, history }) => {
  const [game, setGame] = useState({})
  const [users, setUsers] = useState({})
  const [me, setMe] = useState(undefined)
  const [won, setWon] = useState(false)
  const [gameSecondsOffset, setGameSecondsOffset] = useState(0)
  const [localStartTime, setLocalStartTime] = useState(undefined)
  const _userToken = localStorage.getItem('userToken')
  const duration = useDuration(localStartTime, game.duration_seconds, gameSecondsOffset)
  const _crossword = useRef(null)
  const _socket = useRef(null)
  const [offline, setOffline] = useState(false)
  const [offlineUpdates, setOfflineUpdates] = useLocalStorage(`offline-${match.params.gameId}`, [])
  const [settings, setSettings] = useLocalStorage('settings', {}, 0)
  const [ready, setReady] = useState(false)

  const downClues = useMemo(() => {
    if (!game.down) {
      return {}
    }
    const clues = {}
    Object.keys(game.down).forEach(key => {
      const { row, col } = game.down[key]
      if (!clues[row]) {
        clues[row] = {}
      }
      clues[row][col] = key
    })
    return clues
  }, [game.down])

  const acrossClues = useMemo(() => {
    if (!game.across) {
      return {}
    }
    const clues = {}
    Object.keys(game.across).forEach(key => {
      const { row, col } = game.across[key]
      if (!clues[row]) {
        clues[row] = {}
      }
      clues[row][col] = key
    })
    return clues
  }, [game.across])

  const onClickTimer = () => {
    setSettings(settings => ({
      ...settings,
      timer: !settings.timer,
    }))
  }

  useEffect(() => {
    if (!match.params.gameId) return
    loadGame()
    return () => {
      if (_socket.current) {
        _socket.current.disconnect()
      }
    }
  }, [match.params.gameId])

  useEffect(() => {
    if (game.board && game.board === game.solution) {
        setWon(true)
    }
  }, [game.board])

  useEffect(() => {
    if (!won) {
        return
    }
    celebrate()
  }, [won])

  useEffect(() => {
    if (!offlineUpdates.length) {
      return
    }
    
    if (!_socket.current) {
      return
    }

    // if connected, send up the wire
    processOfflineUpdates()
  }, [offlineUpdates.length, _socket.current])

  // effect to reconnect socket after disconnected
  useEffect(() => {
    if (!offline) {
      return
    }
    const intervalId = window.setInterval(loadGame, RECONNECT_INTERVAL_MS)
    return () => {
      if (intervalId) {
        window.clearInterval(intervalId)
      }
    }
  }, [offline])

  useEffect(() => {
    if (!_crossword.current) {
      return
    }
    if (!offlineUpdates.length) {
      return
    }
    if (!ready) {
      return
    }
    // draw locally
    const { board } = _crossword.current.state
    const newBoard = { ...board }
    offlineUpdates.forEach(({ row, col, value }) => {
      const val = value === '-' ? '' : value
      if (!newBoard[row]) {
        newBoard[row] = {}
      }
      newBoard[row][col] = val
    })
    _crossword.current.setState({ board: newBoard })
  }, [offlineUpdates.length, _crossword.current, ready])

  const getColor = (usedColors) => {
    const _baseRandomColor = () => {
      const idx = Math.floor(Math.random() * COLORS.length)
      return COLORS[idx]
    }
    const { color: preferredColor } = settings

    // if (window.localStorage && typeof window.localStorage.getItem === 'function' &&
    //     window.localStorage.getItem('myColor')) {
    //   return window.localStorage.getItem('myColor')
    // }

    // if theres no remaining colors, return preferred or random
    const remainingColors = COLORS.filter(c => !usedColors.has(c))

    if (!remainingColors.length) {
      return preferredColor || _baseRandomColor()
    }

    // if there is a preferred color, return it if it's available
    if (preferredColor && remainingColors.includes(preferredColor)) {
      return preferredColor
    }

    // otherwise, return a random remaining color
    const idx = Math.floor(Math.random() * remainingColors.length)
    return remainingColors[idx]
  }

  const setColor = (color) => {
    if (!window.localStorage || typeof window.localStorage.setItem !== 'function') {
      return
    }
    window.localStorage.setItem('myColor', color)
    setMe({ color })
    if (!_socket.current) {
      return
    }
    _socket.current.emit('setcolor', color)
  }

  const connectSocket = (game) => {
    const { short_id: shortId } = game
    _socket.current = io(SOCKET_URL)
    _socket.current.on('connect', () => {
      _socket.current.emit('loadgame', { gameId: shortId, userToken: _userToken })
      posthog.capture('socket_connected', { gameId: shortId })
      if (!localStartTime) {
        setLocalStartTime(Date.now())
      }
      setOffline(false)
    })
    _socket.current.on('reconnect', () => {
      _socket.current.emit('loadgame', { gameId: shortId, userToken: _userToken })
      if (!localStartTime) {
        setLocalStartTime(Date.now())
      }
      setOffline(false)
    })
    _socket.current.on('disconnect', () => {
      setOffline(true)
    })
    _socket.current.on('users', users => {
      setUsers(users)
    })
    _socket.current.on('doneloading', users => {
      const color = getColor(new Set(Object.values(users).map(u => u.color)))
      setColor(color)
      _crossword.current.setInitialPosition()
      setReady(true)
    })
    _socket.current.on('boardupdate', ({ row, col, value, socketId }) => {
      if (_socket.id === socketId) {
        // we sent this update, no need to process again
        return
      }
      const { board } = _crossword.current.state
      const newBoard = { ...board }
      let val = value
      if (value === '-') {
        val = ''
      }
      newBoard[row][col] = val
      _crossword.current.setState({ board: newBoard })
      const boardStr = Object.values(newBoard)
        .flatMap(arr => arr.map(item => item === '' ? '.' : item))
        .join('')
      if (boardStr === game.solution) {
        setWon(true)
      }
      // drop from offline update
      setOfflineUpdates(offlineUpdates => 
        offlineUpdates.filter(({ row: offlineRow, col: offlineCol }) => (
          row !== parseInt(offlineRow) && col !== parseInt(offlineCol)
        )
      ))
    })
    _socket.current.on('boardupdates', updates => {
      const { board } = _crossword.current.state
      const newBoard = { ...board }
      updates.forEach(({ row, col, value }) => {
        let val = value
        if (value === '-') {
          val = ''
        }
        newBoard[row][col] = val
      })
      _crossword.current.setState({ board: newBoard })
      const boardStr = Object.values(newBoard)
        .flatMap(arr => arr.map(item => item === '' ? '.' : item))
        .join('')
      if (boardStr === game.solution) {
        setWon(true)
      }
    })
    _socket.current.on('sendpings', ({ row, col }) => {
      const { pingKeys } = _crossword.current.state
      const newPingKeys = [...pingKeys]
      newPingKeys[row][col] = shortid.generate()
      _crossword.current.setState({ pingKeys: newPingKeys })
    })
    _socket.current.on('game_seconds_offset', offset => {
      setGameSecondsOffset(offset)
    })
  }

  const loadGame = async () => {
    const { gameId } = match.params

    if (!gameId) {
      return
    }

    try {
      // TODO: add timeout?
      const game = await GameService.getGameInfo(gameId)
      if (game) {
        localStorage.setItem('lastGameId', gameId)
        addRecentGame(game)
        setGame(game)
        connectSocket(game)
      } else {
        throw new Error('not found')
      }
    } catch (err) {
      if (offline) {
        // already offline, dont reset state
        return
      }
      if (err.message === 'Failed to fetch') {
        const cachedGame = getCachedGame(gameId)
        if (cachedGame) {
          setOffline(true)
          setGame(cachedGame)
          setUsers({
            [OFFLINE_ID]: {
              color: settings.color,
              row: 0,
              col: 0,
            }
          })
          setColor(settings.color)
          _crossword.current.setInitialPosition()
          setReady(true)
        }
      } else {
        console.error(`Error loading game: ${err.message}`)
        alert('Invalid game ID!')
        history.push('/game/continue')
      }
    }
  }

  const processOfflineUpdates = () => {
    // step 1. consolidate
    const boardMap = {}
    offlineUpdates.forEach(({ row, col, value }) => {
      if (!boardMap[row]) {
        boardMap[row] = {}
      }
      boardMap[row][col] = value
    })

    // step 2. deliver
    // TODO: batch delivery
    const gameId = game.short_id
    Object.keys(boardMap).forEach(row => {
      Object.keys(boardMap[row]).forEach(col => {
        const value = boardMap[row][col]
        _socket.current.emit('updateboard', {
          gameId,
          row: parseInt(row),
          col: parseInt(col),
          value,
        })
      })
    })
  }

  const updateLetter = (row, col, val) => {
    const { gameId } = match.params
    let value = val
    if (!val || !val.trim()) {
      value = '-'
    }
    const { board } = _crossword.current.state
    // window.game = game
    // window.board = board
    const update = { gameId, row, col, value }
    if (!_socket.current) {
      setOfflineUpdates(offlineUpdates => [
        ...offlineUpdates,
        update,
      ])
      return
    }
    // TODO: optimize this, still slow
    // TODO: dont need to reset state on boardUpdate when its the same user
    // backend can stop sending this to the updating client
    // TODO: removed, bc its already happening in crossword.js
    // const newBoard = { ...board }
    // newBoard[row][col] = value === '-' ? '' : value
    // _crossword.current.setState({ board: newBoard })

    _socket.current.emit('updateboard', { gameId, row, col, value })
    const boardStr = Object.values(board)
      .flatMap(arr => arr.map(item => item === '' ? '.' : item))
      .join('')
    if (boardStr === game.solution) {
      setWon(true)
    }
  }

  const updatePos = (row, col) => {
    const socketId = _socket.current?.id || OFFLINE_ID
    setUsers(users => ({
      ...users,
      [socketId]: {
        ...users[socketId],
        row,
        col,
      }
    }))
    if (!_socket.current) {
      return
    }
    _socket.current.emit('updatepos', { row, col })
  }

  const emitPing = (row, col) => {
    if (!_socket.current) {
      return
    }
    _socket.current.emit('emitping', { row, col })
    posthog.capture('ping_created', { gameId: game.short_id, row, col })
  }

  const { gameId } = match.params
  const statusString = game.short_id ? game.title.trim() || 'Untitled' : 'Loading...'

  if (!gameId) {
    return <Redirect to='/' />
  }

  // console.log('users: ', users)
  // console.log('socket id: ', _socket.current?.id)

  return (
    <div className='GameWindow'>
      <h1>{statusString}</h1>
      <div className='Row'>
        <StyledHomeButton />
        <div className='Col'>
          <span>Game ID: {gameId}</span>
          <span className='Duration' onClick={onClickTimer} style={{ cursor: 'pointer' }}>
            &nbsp;&nbsp;&nbsp;Timer:&nbsp;
            {settings.timer 
              ? <span style={{ fontSize: '1em' }}>{duration}</span>
              : <span style={{ fontSize: '1em', fontStyle: 'italic' }}>hidden</span>}
          </span>
          {!offline && (
            <span className='UserString'>
              Players: {Object.keys(users).length}&nbsp;&nbsp;&nbsp;&nbsp;
            </span>
          )}
          {offline && (
            <span className='UserString'>
              <strong>⚠️ OFFLINE ⚠️</strong> 
            </span>
          )}
        </div>
        <StyledShareButton />
      </div>
      <Crossword
        ref={_crossword}
        {...game}
        clues={{ down: downClues, across: acrossClues }}
        users={users}
        me={me}
        updateLetter={updateLetter}
        updatePos={updatePos}
        emitPing={emitPing}
        settings={settings} />
    </div>
  )
}

export default Game
